/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
*/

import { useEffect, useState, Dispatch, SetStateAction } from "react";

/**
 * Custom hook for managing tab state with numeric indices.
 *
 * @param tabsLength - Total number of tabs
 * @param defaultIndex - Initial tab index (default: 0)
 * @returns Tuple of [activeIndex, setActiveIndex]
 *
 * @example
 * ```tsx
 * const tabs = ['Tab 1', 'Tab 2', 'Tab 3'];
 * const [activeIndex, setActiveIndex] = useTabActiveIndex(tabs.length);
 *
 * <CosmosTabs activeIndex={activeIndex} onActiveIndexChange={(val) => { if (val !== undefined) setActiveIndex(val); }}>
 *   {tabs.map((tab, index) => (
 *     <Tabs.Button key={index} index={index} label={tab} />
 *   ))}
 * </CosmosTabs>
 * ```
 */
export const useTabActiveIndex = (
	tabsLength: number,
	defaultIndex = 0,
): readonly [number, Dispatch<SetStateAction<number>>] => {
	const [activeIndex, setActiveIndex] = useState(defaultIndex);

	// Reset to first tab if current index is out of bounds
	useEffect(() => {
		if (activeIndex >= tabsLength && tabsLength > 0) {
			setActiveIndex(0);
		}
	}, [tabsLength, activeIndex]);

	return [activeIndex, setActiveIndex] as const;
};
